/**
 * Download menu for the audit data table.
 *
 * Dropdown with two options:
 *  - Current View: exports the current sheet + run (filter-aware post-Commit 3)
 *  - Full Audit: exports all 5 sheets in one workbook, ignores filters
 *
 * Filter-aware plumbing: when AuditContext adds filters/sort/columns in
 * Commit 3+, the "Current View" handler passes them through. "Full Audit"
 * intentionally ignores all filter state — it is always the complete
 * unfiltered snapshot.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";

import { AuditRunSummary, exportFullAudit, exportVehicles } from "../../api/audit";
import { useAudit } from "../../context/AuditContext";
import { THEME } from "../../config/theme";

type DownloadVariant = "current" | "full";

const SHEET_DISPLAY: Record<string, string> = {
  discrepancies: "Discrepancies",
  pending: "Pending",
  completed: "Completed",
  unclassified: "Unclassified",
  dealer_duplicates: "Dealer_Duplicates",
};

function formatFilename(
  variant: DownloadVariant,
  sheet: string,
  runStartedAt: string | null,
): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  let datePart: string;
  if (runStartedAt) {
    const d = new Date(runStartedAt);
    datePart = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;
  } else {
    const d = new Date();
    datePart = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;
  }
  if (variant === "full") {
    return `INVI_Inventory_Audit_${datePart}.xlsx`;
  }
  const sheetPart = SHEET_DISPLAY[sheet] ?? sheet;
  return `INVI_Inventory_${sheetPart}_${datePart}.xlsx`;
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

interface Props {
  currentRun: AuditRunSummary | null;
}

export default function DownloadMenu({ currentRun }: Props) {
  const {
    currentRunId,
    currentSheet,
    totalMatching,
    vehiclesLoading,
    filters,
    sort,
    search,
  } = useAudit();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const disabled =
    downloading ||
    vehiclesLoading ||
    currentRunId === null;

  const currentViewDisabled = disabled || totalMatching === 0;

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const handleDownload = async (variant: DownloadVariant) => {
    if (currentRunId === null) return;
    setOpen(false);
    setDownloading(true);
    setError(null);
    try {
      let blob: Blob;
      if (variant === "full") {
        // Full Audit intentionally ignores filters — always complete workbook
        blob = await exportFullAudit(currentRunId);
      } else {
        // Current View respects active filters, sort, and search
        const params: Parameters<typeof exportVehicles>[1] = {
          sheet: currentSheet,
        };
        if (filters.rooftop.length > 0) params.rooftop = filters.rooftop.join(",");
        if (filters.vehicle_type.length > 0) params.vehicle_type = filters.vehicle_type.join(",");
        if (filters.flag_type.length > 0) params.flag_type = filters.flag_type.join(",");
        if (sort) params.sort = `${sort.field}:${sort.direction}`;
        if (search.trim() !== "") params.search = search.trim();
        blob = await exportVehicles(currentRunId, params);
      }
      const runStartedAt = currentRun?.started_at ?? null;
      triggerBrowserDownload(blob, formatFilename(variant, currentSheet, runStartedAt));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to download";
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: THEME.borderRadius.md,
    border: "none",
    backgroundColor: THEME.colors.navy,
    color: THEME.colors.white,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    userSelect: "none",
    transition: "opacity 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  };

  const menuStyle: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    minWidth: 260,
    backgroundColor: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    zIndex: 100,
    overflow: "hidden",
  };

  const menuItemStyle = (itemDisabled: boolean): React.CSSProperties => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    border: "none",
    backgroundColor: "transparent",
    cursor: itemDisabled ? "not-allowed" : "pointer",
    fontSize: 13,
    color: itemDisabled
      ? THEME.colors.neutral.light.textSecondary
      : THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    transition: "background-color 0.1s",
  });

  const menuItemLabelStyle: React.CSSProperties = {
    fontWeight: 500,
    marginBottom: 2,
  };

  const menuItemSubtitleStyle: React.CSSProperties = {
    fontSize: 12,
    color: THEME.colors.neutral.light.textSecondary,
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        style={buttonStyle}
      >
        <Download size={14} strokeWidth={2} />
        {downloading ? "Preparing..." : "Download"}
        <ChevronDown size={14} strokeWidth={2} />
      </button>

      {open && (
        <div style={menuStyle} role="menu">
          <button
            type="button"
            role="menuitem"
            disabled={currentViewDisabled}
            onClick={() => handleDownload("current")}
            onMouseEnter={(e) => {
              if (!currentViewDisabled) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  THEME.colors.neutral.light.background;
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                "transparent";
            }}
            style={menuItemStyle(currentViewDisabled)}
          >
            <div style={menuItemLabelStyle}>Current View</div>
            <div style={menuItemSubtitleStyle}>
              {currentViewDisabled
                ? "No matching vehicles to export"
                : "Just the current sheet"}
            </div>
          </button>
          <div
            style={{
              height: 1,
              backgroundColor: THEME.colors.silver,
            }}
          />
          <button
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => handleDownload("full")}
            onMouseEnter={(e) => {
              if (!disabled) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  THEME.colors.neutral.light.background;
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                "transparent";
            }}
            style={menuItemStyle(disabled)}
          >
            <div style={menuItemLabelStyle}>Full Audit</div>
            <div style={menuItemSubtitleStyle}>
              All 5 sheets in one workbook
            </div>
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            fontSize: 12,
            color: THEME.colors.semantic.danger,
            whiteSpace: "nowrap",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
